/**
 * Componente de Lista Híbrida para Painéis de Análise
 *
 * Modo Inline: Paginação simples (compatível com WordPress)
 * Modo Fullscreen: Dialog com virtualização via react-virtuoso (performance)
 *
 * Substitui VirtualizedAnalysisList para resolver conflitos de CSS com WordPress.
 */

import React, { useState, useMemo, useRef, useCallback, memo } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Pagination,
  Tooltip,
  Fade,
  useMediaQuery,
  useTheme
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import CloseIcon from '@mui/icons-material/Close'

// Itens por página no modo inline
const ITEMS_PER_PAGE = 15

interface HybridAnalysisListProps<T> {
  /** Array de itens a serem exibidos */
  items: T[]
  /** Função para renderizar cada item */
  renderItem: (item: T, index: number) => React.ReactNode
  /** Mensagem quando a lista está vazia */
  emptyMessage?: string
  /** Submensagem quando a lista está vazia */
  emptySubMessage?: string
  /** Callback para limpar filtros */
  onClearFilters?: () => void
  /** Título para o modal fullscreen */
  fullscreenTitle?: string
  /** Itens por página no modo inline (default: 15) */
  itemsPerPage?: number
  /** Controle externo do modo fullscreen — quando true, abre o dialog */
  isFullscreenOpen?: boolean
  /** Callback chamado quando o fullscreen externo deve fechar */
  onFullscreenClose?: () => void
}

function HybridAnalysisListInner<T>({
  items,
  renderItem,
  emptyMessage = 'Nenhum item encontrado',
  emptySubMessage = 'Tente ajustar os filtros',
  onClearFilters,
  fullscreenTitle = 'Visualização Completa',
  itemsPerPage = ITEMS_PER_PAGE,
  isFullscreenOpen: externalFullscreenOpen,
  onFullscreenClose
}: HybridAnalysisListProps<T>) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [internalFullscreenOpen, setInternalFullscreenOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Fullscreen efetivo: externo OU interno (qualquer um abre o dialog)
  const isFullscreenOpen = !!(externalFullscreenOpen || internalFullscreenOpen)
  const virtuosoRef = useRef<VirtuosoHandle>(null)

  // Calcular número total de páginas
  const totalPages = Math.ceil(items.length / itemsPerPage)

  // Itens da página atual (modo inline)
  const currentPageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return items.slice(startIndex, startIndex + itemsPerPage)
  }, [items, currentPage, itemsPerPage])

  // Ajustar página quando items mudam
  useMemo(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [totalPages, currentPage])

  // Handlers
  const handleOpenFullscreen = useCallback(() => {
    setInternalFullscreenOpen(true)
  }, [])

  const handleCloseFullscreen = useCallback(() => {
    setInternalFullscreenOpen(false)
    onFullscreenClose?.()
  }, [onFullscreenClose])

  // Lista vazia
  if (items.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', bgcolor: '#f5f5f5' }}>
        <FilterListIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: '#bdbdbd', mb: 1 }} />
        <Typography variant="body1" sx={{ color: '#666', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {emptyMessage}
        </Typography>
        <Typography variant="body2" sx={{ color: '#999', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {emptySubMessage}
        </Typography>
        {onClearFilters && (
          <Button variant="outlined" size="small" onClick={onClearFilters}>
            Limpar filtros
          </Button>
        )}
      </Paper>
    )
  }

  return (
    <>
      {/* MODO INLINE - Lista Paginada */}
      <Box>
        {/* Lista de itens da página atual */}
        {currentPageItems.map((item, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            {renderItem(item, (currentPage - 1) * itemsPerPage + index)}
          </Box>
        ))}

        {/* Paginação e botão fullscreen */}
        {(totalPages > 1 || items.length > 5) && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            mt: 2,
            py: 1.5,
            bgcolor: '#f5f5f5',
            borderRadius: 1
          }}>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            )}
            <Tooltip title="Abrir em tela cheia para visualização completa">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FullscreenIcon />}
                onClick={handleOpenFullscreen}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                {isMobile ? '' : 'Tela cheia'}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* MODAL FULLSCREEN */}
      <Dialog
        fullScreen
        open={isFullscreenOpen}
        onClose={handleCloseFullscreen}
        TransitionComponent={Fade}
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: '#f8f9fa'
          }
        }}
      >
        {/* AppBar */}
        <AppBar sx={{ position: 'relative', bgcolor: '#1976d2' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseFullscreen}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {fullscreenTitle}
            </Typography>
            <Chip
              label={`${items.length} itens`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
            />
          </Toolbar>
        </AppBar>

        {/* Conteúdo do Fullscreen com Virtuoso */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
          p: 1
        }}>
          <Virtuoso
            ref={virtuosoRef}
            data={items}
            itemContent={(index, item) => (
              <Box sx={{ py: 0.5 }}>
                {renderItem(item, index)}
              </Box>
            )}
            style={{ height: '100%' }}
            overscan={200}
          />
        </Box>
      </Dialog>
    </>
  )
}

// Exportar memoizado
const HybridAnalysisList = memo(HybridAnalysisListInner) as typeof HybridAnalysisListInner

export default HybridAnalysisList
